import type { CategoryInput, ServiceInput } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const CATALOG_ROUTES = {
  CATEGORIES: '/categorias',
  SERVICES: '/servicos',
  SERVICE_NEW: '/servicos/novo',
  SERVICE_DETAIL_PATTERN: '/servicos/:id',
  service: (id: string) => `/servicos/${id}`,
} as const;

const CATEGORIES = '/api/v1/categories';
const SERVICES = '/api/v1/services';

export interface Category {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  image: string | null;
  isActive: boolean;
  servicesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Service {
  id: string;
  companyId: string;
  categoryId: string | null;
  categoryName: string | null;
  unitId: string | null;
  unitName: string | null;
  name: string;
  description: string | null;
  images: string[];
  price: number;
  durationMinutes: number;
  maxDiscountPercent: number;
  autoDiscountPercent: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Paged<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface ServicesQuery {
  page: number;
  perPage?: number;
  search?: string;
  categoryId?: string;
  unitId?: string;
  /** `popular` = mais lancado em comanda primeiro. Padrao da API e por nome. */
  sort?: 'name' | 'popular';
}

export const catalogService = {
  listCategories(params: { page: number; perPage?: number; search?: string }) {
    // Sem `perPage` a lista vem inteira: quem chama assim usa as categorias
    // para preencher um select, nao para paginar.
    const query = new URLSearchParams({
      page: String(params.page),
      perPage: String(params.perPage ?? 100),
    });
    if (params.search) query.set('search', params.search);

    return apiClient.get<Paged<Category>>(`${CATEGORIES}?${query.toString()}`);
  },

  createCategory: (input: CategoryInput) => apiClient.post<Category>(CATEGORIES, input),
  updateCategory: (id: string, input: Partial<CategoryInput>) =>
    apiClient.patch<Category>(`${CATEGORIES}/${id}`, input),
  removeCategory: (id: string) => apiClient.delete<{ message: string }>(`${CATEGORIES}/${id}`),

  listServices(params: ServicesQuery) {
    const query = new URLSearchParams({ page: String(params.page) });
    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.search) query.set('search', params.search);
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.unitId) query.set('unitId', params.unitId);
    if (params.sort) query.set('sort', params.sort);

    return apiClient.get<Paged<Service>>(`${SERVICES}?${query.toString()}`);
  },

  getService: (id: string) => apiClient.get<Service>(`${SERVICES}/${id}`),
  createService: (input: ServiceInput) => apiClient.post<Service>(SERVICES, input),
  updateService: (id: string, input: Partial<ServiceInput>) =>
    apiClient.patch<Service>(`${SERVICES}/${id}`, input),
  removeService: (id: string) => apiClient.delete<{ message: string }>(`${SERVICES}/${id}`),
};

const KEYS = {
  categories: ['catalog', 'categories'] as const,
  services: ['catalog', 'services'] as const,
  service: (id: string) => ['catalog', 'services', id] as const,
};

export function useCategories(
  params: { page: number; perPage?: number; search?: string } = { page: 1 },
) {
  return useQuery({
    queryKey: [...KEYS.categories, params],
    queryFn: () => catalogService.listCategories(params),
    placeholderData: (previous) => previous,
  });
}

function useCatalogMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    // Categoria e servico se referenciam: invalida os dois.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalog'] }),
  });
}

export function useCreateCategory() {
  return useCatalogMutation((input: CategoryInput) => catalogService.createCategory(input));
}

export function useUpdateCategory() {
  return useCatalogMutation(({ id, input }: { id: string; input: Partial<CategoryInput> }) =>
    catalogService.updateCategory(id, input),
  );
}

export function useDeleteCategory() {
  return useCatalogMutation((id: string) => catalogService.removeCategory(id));
}

/** Um dia: o catalogo muda pouco e a lista carrega thumb a cada abertura. */
const ONE_DAY = 24 * 60 * 60 * 1000;

/**
 * `enabled: false` na pagina publica: la o catalogo vem do conteudo publicado.
 *
 * A lista fica um dia em cache - o que a mantem correta nao e o relogio, e a
 * invalidacao: toda escrita de servico ou categoria derruba `KEYS.all`.
 */
export function useServices(params: ServicesQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEYS.services, params],
    queryFn: () => catalogService.listServices(params),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
  });
}

export function useServiceDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.service(id ?? ''),
    queryFn: () => catalogService.getService(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateService() {
  return useCatalogMutation((input: ServiceInput) => catalogService.createService(input));
}

export function useUpdateService() {
  return useCatalogMutation(({ id, input }: { id: string; input: Partial<ServiceInput> }) =>
    catalogService.updateService(id, input),
  );
}

export function useDeleteService() {
  return useCatalogMutation((id: string) => catalogService.removeService(id));
}
